import React from 'react';
import data from '../../../../utils/dbSchema';
import Layout from '../../../Layout';
import { Box } from '../../../UI';
import CashOnCash from './CashOnCash';
import Details from './Details';
import Exit from './Exit';
import General from './General';
import Header from './Header';
import Leverage from './Leverage';
import Output from './Output';
import Pricing from './Pricing';
import Tax from './Tax';
import PageTitle from '../../../Layout/PageTitle';
import NavigateBack from '../../../UI/NavigateBack';

const vatOptions = [
	{
		value: 1.2,
		label: 'Yes',
	},
	{
		value: 1,
		label: 'No',
	},
];

export default function Analysis({ match }) {
	const propertyData = data.properties.find(property => {
		return match.params.propertyId === property.id;
	});
	const [variables, setVariables] = React.useState({
		purchasePrice: propertyData.analysis.pricing.purchasePrice,
		transationCost: propertyData.analysis.pricing.transationCost,
	});
	const [exitCapRate, setExitCapRate] = React.useState(propertyData.analysis.exit.capRate);
	const [vat, setVat] = React.useState(1);

	const handleVariable = (value, key) => {
		setVariables({
			...variables,
			[key]: value,
		});
	};

	const handleVat = event => {
		setVat(event.target.value);
	};

	// Destructure calculation variables
	const { details, output, general, tax, exit, leverage } = propertyData.analysis;
	const { purchasePrice, transationCost } = variables;
	const gri = details.GRI.gbp;
	const gla = details.GLA;
	const stampDuty = (0.02 * 99999 + (purchasePrice - 250000) * 0.05) * vat;
	const ltppPercentSen = leverage.senior.ltppPercent;
	const ltppPercentMez = leverage.mezz.ltppPercent;
	const upFrontPercentSen = leverage.senior.upFrontPercent;
	const upFrontPercentMez = leverage.mezz.upFrontPercent;
	// Pre-calculations
	const ltPPSen = ltppPercentSen * purchasePrice;
	const ltPPMez = ltppPercentMez * purchasePrice - ltPPSen;
	const upFrontFeeSen = upFrontPercentSen * ltPPSen;
	const upFrontFeeMez = upFrontPercentMez * ltPPMez;
	// Calculations
	const totalCost = transationCost + purchasePrice + stampDuty + upFrontFeeSen + upFrontFeeMez;
	const TCperPP = ((transationCost / purchasePrice) * 100).toFixed(2);
	const grossCapRate = ((gri / purchasePrice) * 100).toFixed(2);
	const netCapRate = ((gri / totalCost) * 100).toFixed(2);
	const grossPricePerSqFt = (purchasePrice / gla / 11.1).toFixed(0);
	const netPricePerSqFt = (totalCost / gla / 11.1).toFixed(0);
	const grossPurchaseMulti = (purchasePrice / gri).toFixed(1);
	const netPurchaseMulti = (totalCost / gri).toFixed(1);
	const equityInvested = totalCost - ltPPSen - ltPPMez;
	const exitPrice = exit.gri / (exitCapRate / 100);
	const exitPricePerSqFt = exitPrice / gla / 11.1;

	// Final data
	const pricingData = {
		purchasePrice,
		transationCost,
		totalCost: totalCost.toFixed(0),
		TCperPP,
		grossCapRate,
		netCapRate,
		grossPricePerSqFt,
		netPricePerSqFt,
		grossPurchaseMulti,
		netPurchaseMulti,
	};
	const outputData = {
		equityInvested: equityInvested.toFixed(0),
		...output,
	};
	const taxData = {
		stampDuty: stampDuty.toFixed(0),
		vatOptions,
		vat,
		handleVat,
		...tax,
	};
	const exitData = {
		exitCapRate,
		exitPrice: exitPrice.toFixed(0),
		exitPricePerSqFt: exitPricePerSqFt.toFixed(0),
		...exit,
	};
	const leverageData = {
		senior: { ...leverage.senior, ltPPSen, upFrontFeeSen },
		mezz: { ...leverage.mezz, ltPPMez, upFrontFeeMez },
	};

	return (
		<Layout>
			<Box p="30px">
				<PageTitle title={propertyData.id} action={<NavigateBack slug={`/market-search/${propertyData.id}`} />}>
					<Header data={propertyData} />
				</PageTitle>
				<Box display="flex" flexDirection="row" flexWrap="wrap" mb="1rem">
					<Box width={['50%', '50%', '50%', '33.33%']} pr="15px">
						<Details data={details} />
					</Box>
					<Box width={['50%', '50%', '50%', '33.33%']} pr={[0, 0, 0, '15px']}>
						<Output data={outputData} />
					</Box>
					<Box width={['100%', '100%', '100%', '33.33%']} mt={['15px', '15px', '15px', 0]}>
						<CashOnCash />
					</Box>
				</Box>
				<Box display="flex" flexDirection="row" mb="1rem">
					<Box width="50%" pr="0.5rem">
						<Pricing data={pricingData} handleVariable={handleVariable} />
					</Box>
					<Box width="50%" pl="0.5rem">
						<General data={general} />
					</Box>
				</Box>
				<Box display="flex" flexDirection="row" mb="1rem">
					<Box width="50%" pr="0.5rem">
						<Tax data={taxData} />
					</Box>
					<Box width="50%" pl="0.5rem">
						<Exit data={exitData} setExitCapRate={setExitCapRate} />
					</Box>
				</Box>
				<Box><Leverage data={leverageData} /></Box>
			</Box>
		</Layout>
	);
}
